import { useEffect, useState } from 'react';

import { NOT_IN_A_TEAM_VALUE, TeamOption, TeamsFilterProps } from './TeamsFilter.decl';

import { Flex } from '@aircall/tractor-v2';
import { capitalizeFirstLetter, MultiSearchSelect, useGraphQuery } from '@dashboard/library';
import { SearchableSortDirection, SearchableTeamsSortableFields } from '@generated/globalTypes';
import { SearchTeamsQuery, SearchTeamsQueryVariables } from '@generated/SearchTeamsQuery';
import { SEARCH_TEAMS_QUERY } from '@graphql/queries/SearchTeamsQuery';
import { getSearchTeamsFilters } from '@helpers/search.helpers';
import { Emoji } from 'emoji-mart';
import { useTranslation } from 'react-i18next';

export function TeamsFilter({ onSelect, initialTeamsIds }: TeamsFilterProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const { data, loading } = useGraphQuery<SearchTeamsQuery, SearchTeamsQueryVariables>(
    SEARCH_TEAMS_QUERY,
    {
      variables: {
        filter: getSearchTeamsFilters(query),
        sort: [
          {
            field: SearchableTeamsSortableFields.name,
            direction: SearchableSortDirection.asc,
          },
        ],
      },
    }
  );

  function handleSearch(q: string) {
    setQuery(q);
  }

  const total = data?.searchTeams?.total || 0;

  const options: TeamOption[] = data?.searchTeams?.items
    ? [
        {
          label: t('users.filters.teams_filter.not_in_a_team'),
          value: NOT_IN_A_TEAM_VALUE,
          imageName: null,
        },
        ...data.searchTeams.items.map((item) => ({
          label: item.name,
          value: item.ID,
          imageName: item.imageName,
        })),
      ]
    : [];

  function renderItemPrefix(option: TeamOption) {
    if (!option.imageName) {
      return null;
    }
    const emojiSize = 24;
    return (
      <Flex w={emojiSize} h={emojiSize}>
        <Emoji set='apple' emoji={option.imageName} size={emojiSize} />
      </Flex>
    );
  }

  function handleSelect(keys: string[]) {
    setSelectedKeys(keys);
    onSelect(keys);
  }

  useEffect(() => {
    setSelectedKeys(initialTeamsIds ?? []);
  }, [initialTeamsIds]);

  return (
    <MultiSearchSelect<TeamOption>
      defaultSelectedKeys={initialTeamsIds || undefined}
      selectedKeys={selectedKeys}
      minWidth='160px'
      texts={{
        placeholder: capitalizeFirstLetter(t('generic.teams')),
        item: t('generic.team'),
        items: t('generic.teams'),
        result: t('generic.result'),
        results: t('generic.results'),
      }}
      options={options}
      isSearch={!!query.length}
      loading={loading}
      renderItemPrefix={renderItemPrefix}
      onSearch={handleSearch}
      onSelect={handleSelect}
      resultsCount={query.length ? options.length : total}
    />
  );
}
